import { useMemo } from 'react';

import { CalculatorCalendarCell, type DayCell } from './calculator-calendar-cell';
import type { ResolveProceduralDateResult } from '../../../../../backend/src/matter/procedural-timeline/date-resolution';

interface CalculatorCalendarProps {
  result: ResolveProceduralDateResult;
  startDate: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
}

const WEEKDAY_LABELS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export function CalculatorCalendar({ result, startDate, selectedDate, onSelectDate }: CalculatorCalendarProps) {
  const months = useMemo(() => buildMonths(result, startDate), [result, startDate]);
  const selectedCivilTime = toCivilDate(selectedDate).getTime();

  return (
    <div className="flex flex-col gap-3">
      {months.map((month) => (
        <div key={`${month.year}-${month.monthIndex}`} className="rounded-lg border border-gray-200 p-3">
          <div className="mb-2 text-center text-sm font-medium text-gray-800">
            {monthLabel(month.year, month.monthIndex)}
          </div>
          <div className="mb-1 grid grid-cols-7 gap-1 text-[10px] font-medium text-gray-400">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="px-2 text-center">
                {label}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {month.cells.map((cell, i) => (
              <CalculatorCalendarCell
                key={i}
                cell={cell}
                isSelected={cell.inMonth && cell.date.getTime() === selectedCivilTime}
                onSelect={onSelectDate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface MonthGrid {
  year: number;
  monthIndex: number;
  cells: DayCell[];
}

function buildMonths(result: ResolveProceduralDateResult, startDate: Date): MonthGrid[] {
  const startCivil = toCivilDate(startDate);
  const resultCivil = toCivilDate(result.resultDate);
  const withinTime =
    result.withinDate !== null && result.withinDate !== undefined ? toCivilDate(result.withinDate).getTime() : null;
  const lo = startCivil.getTime() <= resultCivil.getTime() ? startCivil : resultCivil;
  const hi = startCivil.getTime() <= resultCivil.getTime() ? resultCivil : startCivil;

  const countedKeys = new Map<string, number>();
  result.countedDates.forEach((d, i) => countedKeys.set(civilKey(toCivilDate(d)), i + 1));
  const skippedKeys = new Map<string, { weekend: boolean; holiday: boolean; name?: string }>();
  for (const skip of result.skippedDates) {
    const k = civilKey(toCivilDate(skip.date));
    const existing = skippedKeys.get(k) ?? { weekend: false, holiday: false };
    if (skip.reason === 'weekend') existing.weekend = true;
    if (skip.reason === 'public-holiday') {
      existing.holiday = true;
      existing.name = skip.name;
    }
    skippedKeys.set(k, existing);
  }
  const observedKeys = new Map<string, string>();
  if (!result.publicHolidaysCount) {
    for (const oh of result.observedHolidays) {
      observedKeys.set(civilKey(toCivilDate(oh.date)), oh.name);
    }
  }

  const months: MonthGrid[] = [];
  let cursor = new Date(Date.UTC(lo.getUTCFullYear(), lo.getUTCMonth(), 1));
  const endMonth = new Date(Date.UTC(hi.getUTCFullYear(), hi.getUTCMonth(), 1));

  while (cursor.getTime() <= endMonth.getTime()) {
    const monthIndex = cursor.getUTCMonth();
    const year = cursor.getUTCFullYear();
    const firstWeekday = (new Date(Date.UTC(year, monthIndex, 1)).getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
    const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    const cells: DayCell[] = [];
    for (let i = 0; i < totalCells; i += 1) {
      const dayNumber = i - firstWeekday + 1;
      const inMonth = dayNumber >= 1 && dayNumber <= daysInMonth;
      if (!inMonth) {
        cells.push(emptyCell(year, monthIndex, dayNumber));
        continue;
      }
      const date = new Date(Date.UTC(year, monthIndex, dayNumber));
      const inWindow = date.getTime() >= lo.getTime() && date.getTime() <= hi.getTime();
      const key = civilKey(date);
      const isStart = date.getTime() === startCivil.getTime();
      const isResult = date.getTime() === resultCivil.getTime();
      const isWithin = withinTime !== null && date.getTime() === withinTime;
      const skip = inWindow ? skippedKeys.get(key) : undefined;
      const observedName = observedKeys.get(key);
      const isSkippedWeekend = inWindow && !isStart && !isResult && skip?.weekend === true;
      const isSkippedHoliday =
        inWindow && !isStart && !isResult && (skip?.holiday === true || observedName !== undefined);
      const countPosition = inWindow ? (countedKeys.get(key) ?? null) : null;
      cells.push({
        date,
        inMonth,
        inWindow,
        isStart,
        isResult,
        isWithin,
        isSkippedWeekend,
        isSkippedHoliday,
        holidayName: skip?.name ?? observedName,
        countPosition,
      });
    }
    months.push({ year, monthIndex, cells });
    cursor = new Date(Date.UTC(year, monthIndex + 1, 1));
  }

  return months;
}

function emptyCell(year: number, monthIndex: number, dayNumber: number): DayCell {
  return {
    date: new Date(Date.UTC(year, monthIndex, dayNumber)),
    inMonth: false,
    inWindow: false,
    isStart: false,
    isResult: false,
    isWithin: false,
    isSkippedWeekend: false,
    isSkippedHoliday: false,
    countPosition: null,
  };
}

function toCivilDate(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function civilKey(date: Date): string {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  return `${y}-${m < 10 ? `0${m}` : m}-${d < 10 ? `0${d}` : d}`;
}

function monthLabel(year: number, monthIndex: number): string {
  return new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
